import { computedFrom, observable } from 'aurelia-framework';
import { last } from 'lodash';
import { IDraggableHandler } from '../../components/drag-and-drop/models';
import { IRuleValidationException } from './accounting-rule-error-display';

export class RuleTemplate {

	static getForType(ruleType: string): string {

		switch (ruleType) {

			case 'ServiceTargeting':
				return `from:
	<COSTCENTER>
	# Jos käytät take sum from -määrettä, varmista kausittain, että tilin saldo ja säännön kohdistus ovat oikean merkkiset!
by:
	# Valitse jokin allaolevista:
	# weight      # (kunkin palvelukoodin perään painoarvo)
	# percentage  # (kunkin palvelukoodin perään prosenttiosuus)
	# measure SUORITE in costcenter / costcenter XXX / service   using total # (tai aggregate all/member/external municipalities)
	# mapping from account/accountgroup/accountclass / dimension TARKENNE
	# sum of account/accountgroup/accountclass XXX
to services:
	<SERVICES>
`;

			case 'CostCenterDivision':
				return `from:
    <COSTCENTER>
by:
    # Valitse jokin allaolevista:
    # weight      # (kunkin kohteen perään painoarvo)
    # percentage  # (kunkin kohteen perään prosenttiosuus)
    # sum of accountclass/accountgroup/account XXX
    # measure SUORITE of PALVELU aggregate all/member/external municipalities  /  using total
    use account 9999
to costcenters:
    `;

			case 'DimensionDivision':
				return `from:
    <COSTCENTER>
by:
    # Valitse jokin allaolevista:
    # weight      # (kunkin kohteen perään painoarvo)
    # percentage  # (kunkin kohteen perään prosenttiosuus)
    # measure SUORITE of PALVELU in costcenter/service aggregate all/member/external municipalities  /  using total
to dimension XXX:
    `;

			case 'TypeSetting':
				return `from:
    <COSTCENTER>
    # type External # jos halutaan muuttaa vain ulkoisia
by:
    # Valitse jokin allaolevista:
    # weight      # (kunkin tyypin perään painoarvo)
    # percentage  # (kunkin tyypin perään prosenttiosuus)
to types:
    # PassedOnRevenue
    # PassedOnExpenses
    # InternalRevenue
    # InternalExpenses
    # External
`;

			case 'MunicipalityAllocation':
				return `from:
    <COSTCENTER>
    service <SERVICE>
by:
    # Valitse jokin allaolevista:
    # weight      # (kunkin kuntakoodin perään painoarvo)
    # percentage  # (kunkin kuntakoodin perään prosenttiosuus)
    # population  # (to-kohtaan members ja/tai partialmembers)
    # measure SUORITE of PALVELU in costcenter / costcenter XXX / service
    # mapping from dimension TARKENNE
to municipalities:
<MUNICIPALITIES>
    # External X %  # Ulkokunnat
    # -- tai --
    # members, partialmembers, external`;

			case 'ExternalMunicipalitiesDivision':
				return `from:
    <COSTCENTER>
    municipality external
by:
    sum of accountclass/accountgroup/account XXX
    use account 9999
to municipalities:
    members, partialmembers`;

		case 'MeasureServiceTargeting':
			return `measures SUORITE1, SUORITE2
from:
	<COSTCENTER>
	service <SERVICE>
by:
	sum of account/accountgroup/accountclass XXX
to services:
`;

			case 'MeasureCostCenterDivision':
				return `measures SUORITE1, SUORITE2, ...
from:
	<COSTCENTER>
	service PALVELU
by:
	sum of account/accountgroup/accountclass XXX
	divide to services by sum of account/accountgroup/accountclass XXX
to costcenters:
`

			default: return '';
		}
	}
}

export interface IMissingMunicipalityAllocationRule {
	costCenterName: string;
	costCenterIdentifier: string;
	typeName: string;
	definition: string;
	serviceCode: string;
	serviceName: string;
	customerMunicipalities: { [id: string]: string; };
}

export class MissingMunicipalityAllocationRule implements IMissingMunicipalityAllocationRule {
	static createNew(data: IMissingMunicipalityAllocationRule) {
		const item = new MissingMunicipalityAllocationRule();
		item.costCenterName = data.costCenterName;
		item.costCenterIdentifier = data.costCenterIdentifier;
		item.typeName = data.typeName;
		item.expanded = false;
		item.serviceName = data.serviceName;

		let municipalitiesInRuleDefinition = '';
		for (const i in data.customerMunicipalities) {
			if (data.customerMunicipalities.hasOwnProperty(i)) {
				const code = i;
				const municipalityName = data.customerMunicipalities[i];
				municipalitiesInRuleDefinition += `    # ${code} X%  # ${municipalityName}\n`;
			}
		}

		item.definition = RuleTemplate.getForType('MunicipalityAllocation')
			.replace('<COSTCENTER>', `costcenter ${item.costCenterIdentifier}`)
			.replace('<SERVICE>', data.serviceCode)
			.replace('<MUNICIPALITIES>', municipalitiesInRuleDefinition);

		return item;
	}

	costCenterName: string;
	costCenterIdentifier: string;
	typeName: string;
	definition: string;
	expanded: boolean;
	serviceCode: string;
	serviceName: string;
	customerMunicipalities: { [id: string]: string; };
	validationErrors: IRuleValidationException[];
}

export interface IMissingServiceTargetingRule {
	costCenterName: string;
	costCenterIdentifier: string;
	typeName: string;
	definition: string;
	serviceCodes: string[];
}

export class MissingServiceTargetingRule implements IMissingServiceTargetingRule {
	static createNew(mapping: IMissingServiceTargetingRule) {
		const item = new MissingServiceTargetingRule();
		item.costCenterName = mapping.costCenterName;
		item.costCenterIdentifier = mapping.costCenterIdentifier;
		item.typeName = mapping.typeName;
		item.expanded = false;

		let servicesInRuleDefinition = '';
		mapping.serviceCodes.forEach(element => {
			servicesInRuleDefinition += `    ${element}\n`;
		});

		item.definition = RuleTemplate.getForType('ServiceTargeting')
			.replace('<COSTCENTER>', `costcenter ${item.costCenterIdentifier}`)
			.replace('<SERVICES>', servicesInRuleDefinition);

		return item;
	}

	costCenterName: string;
	costCenterIdentifier: string;
	typeName: string;
	definition: string;
	expanded: boolean;
	serviceCodes: string[];
	validationErrors: IRuleValidationException[];
}

export interface IRuleCostCenter {
	name: string;
	identifier: string;
}

export interface IAccountingRule {
	id: string;
	scopeId: string;
	typeName: string;
	definition: string;
	originalDefinition: string;
	orderNumber: number;
	folderId: string | null;
	costCenters: IRuleCostCenter[];
	isForAllCostCenters: boolean;
	isBypassed?: boolean;
	isVisible: boolean;
	validationErrors: IRuleValidationException[];
	isDirty: boolean;
	editInProgress: boolean;
}

export class AccountingRule implements IAccountingRule, IDraggableHandler {

	static createNew(accountingRuleVM: IAccountingRule) {
		const item = new AccountingRule();
		item.copyDataFrom(accountingRuleVM);
		return item;
	}

	copyDataFrom(source: IAccountingRule) {
		this.id = source.id;
		this.scopeId = source.scopeId;
		this.typeName = source.typeName;
		this.definition = source.definition;
		this.originalDefinition = source.definition;
		this.orderNumber = source.orderNumber;
		this.folderId = source.folderId;
		this.costCenters = source.costCenters;
		this.isForAllCostCenters = source.isForAllCostCenters;
		this.isBypassed = source.isBypassed;
		this.validationErrors = source.validationErrors;
		this.expanded = false;
		this.isVisible = true;
		this.isDirty = false;
		this.editInProgress = false;
	}

	@observable definition: string;
	originalDefinition: string;

	id: string;
	scopeId: string;
	typeName: string;
	orderNumber: number;
	folderId: string | null;
	costCenters: IRuleCostCenter[];
	isForAllCostCenters: boolean;
	isBypassed?: boolean;
	expanded: boolean;
	isVisible: boolean;
	validationErrors: IRuleValidationException[];
	isDirty: boolean;
	editInProgress: boolean;

	isDragged = false;

	@computedFrom('costCenters')
	get costCenterNames(): string[] {
		return this.costCenters.map(cc => cc.identifier + ' ' + (cc.name || "???"));
	}

	onDragStart(event, data: AccountingRule) {
		if (event.stopPropagation) {
			event.stopPropagation();
		}

		this.isDragged = true;

		event.dataTransfer.effectAllowed = 'move';
		const json = JSON.stringify(data);
		event.dataTransfer.setData('application/json', json);
	}

	onDragEnd(event, data: any): void {
		this.isDragged = false;
	}

	definitionChanged(newValue, oldValue) {
		if (newValue == this.originalDefinition) {
			this.isDirty = false;
			this.validationErrors = []; // Clear errors if rule's original state is returned
		} else {
			this.isDirty = true;
		}
	}

	static formatDefinition(definition: string) {
		return definition.replace(new RegExp('\t', 'g'), ' ');
	}
}

export interface IAccountingRuleFolder {
	id: string;
	scopeId: string;
	name: string;
	orderNumber: number;
	isReadOnly: boolean;
	rules: IAccountingRule[];
}

export class AccountingRuleFolder implements IAccountingRuleFolder {
	id: string;
	scopeId: string;
	name: string;
	orderNumber: number;
	isReadOnly: boolean;
	rules: IAccountingRule[];

	static getLastOrderNumber(folder: IAccountingRuleFolder): number {
		const rulesInFolder = folder.rules
			.sort((a, b) => a.orderNumber - b.orderNumber);
		return rulesInFolder.length > 0 
			? last(rulesInFolder).orderNumber + 1
			: 1;
	}
}
